/**
 * IMPAGINAZIONE DELLA STAMPA PER SAFARI (WebKit)
 * ------------------------------------------------
 * In Chrome il pie' di pagina e' il <tfoot> della struttura di stampa, che il browser
 * ripete su ogni foglio. WebKit non ripete <thead>/<tfoot>, ignora i box di margine di
 * `@page` e stampa gli elementi `position: fixed` una volta sola: in Safari il pie' di
 * pagina usciva quindi solo in fondo al report (verificato il 30/09/2026).
 *
 * Qui, solo per WebKit e subito prima di `window.print()`, si decide in JavaScript dove
 * cadono i fogli e si inserisce a ogni fine foglio un blocco "fine pagina": uno spazio
 * vuoto che porta il pie' di pagina in fondo al foglio, una copia del pie' di pagina e un
 * salto pagina forzato. I salti forzati del CSS di stampa vengono neutralizzati (li
 * gestisce l'impaginatore), cosi' non si sommano ai nostri.
 *
 * Per misurare il layout di stampa senza essere in stampa, le regole `@media print`
 * vengono attivate temporaneamente anche a video aggiungendo "screen" alla loro media
 * list: le regole restano al loro posto nella cascata, quindi il layout misurato e'
 * quello di stampa. Le media query `min-width` piu' larghe della pagina stampata vengono
 * spente per lo stesso motivo.
 *
 * WebKit inoltre IGNORA i salti forzati dentro i contenitori flex (verificato: in una
 * colonna flex `break-after: page` non ha effetto). I blocchi "fine pagina" vanno quindi
 * inseriti solo in contenitori a blocco, e i contenitori `flex-col` "neutri" (nessuno
 * spazio fra i figli, nessun allineamento, figli a blocco: disposti esattamente come in un
 * blocco) vengono trasformati in blocchi per la durata della stampa. La trasformazione
 * avviene prima di misurare, quindi le misure valgono per il layout stampato.
 *
 * Tutto cio' che viene aggiunto e' rimosso da `undoSafariPagination` (evento afterprint).
 * Se qualcosa va storto si ricade nel comportamento precedente (pie' di pagina una volta
 * sola in fondo), mai in un report rotto.
 */

/**
 * Altezza di un foglio A4 in px del report, in Safari: e' la leva principale di questo
 * modulo. Con il motore di stampa WebKit e `@page { margin-top: 8mm; margin-bottom: 34px }`
 * lo spazio reale misurato su un report vero e' ~1705px (un nostro foglio da 1699px e'
 * entrato, uno da 1700 no). Si tiene un margine di sicurezza ampio: se un nostro foglio
 * e' anche di poco piu' alto di quello vero, il blocco "fine pagina" (indivisibile)
 * passa intero al foglio dopo e lascia un foglio con il solo pie' di pagina. Il prezzo
 * e' un pie' di pagina ~15-20mm sopra il bordo inferiore invece che appoggiato.
 * Se in Safari compaiono fogli con il solo pie' di pagina, abbassare questo valore.
 */
export const SAFARI_SHEET_HEIGHT = 1620;

/** Larghezza del report (`.print-layout`, `.virtual-page`) in px del report. */
const REPORT_WIDTH = 1180;

/** Larghezza della pagina stampata in px CSS: le media query piu' larghe non valgono. */
const PRINT_VIEWPORT_WIDTH = 1190;

/** Tolleranza in px per confronti fra misure (arrotondamenti del layout). */
const EPS = 1;

const PAGINATED_CLASS = "safari-paginated";
const PAGE_END_CLASS = "safari-page-end";

interface PageBreak {
  /** Elemento che deve iniziare un foglio nuovo; null = fine del report. */
  before: Element | null;
  /** Contenitore in cui inserire il blocco "fine pagina" (per la fine del report). */
  parent: Element;
  /** Altezza dello spazio vuoto, in px del report. */
  spacer: number;
}

type MediaToggle = () => void;

/** Rende le regole di stampa attive anche a video. Restituisce la funzione che annulla. */
function emulatePrintMedia(): MediaToggle {
  const undo: MediaToggle[] = [];
  const visit = (rules: CSSRuleList) => {
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSMediaRule)) continue;
      const media = rule.media;
      const text = media.mediaText.toLowerCase();
      if (/\bprint\b/.test(text) && !/\bscreen\b/.test(text) && !/\bnot\b/.test(text)) {
        media.appendMedium("screen");
        undo.push(() => media.deleteMedium("screen"));
      } else {
        const minWidth = /min-width:\s*(\d+(?:\.\d+)?)px/.exec(text);
        if (minWidth != null && parseFloat(minWidth[1]) > PRINT_VIEWPORT_WIDTH) {
          const original = media.mediaText;
          media.mediaText = "not all";
          undo.push(() => {
            media.mediaText = original;
          });
        }
      }
      visit(rule.cssRules);
    }
  };
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      visit(sheet.cssRules);
    } catch (e) {
      // Fogli di stile di altri domini (es. Google Fonts): non leggibili, non servono.
    }
  }
  return () => undo.reverse().forEach((f) => f());
}

function isInFlow(el: Element, style: CSSStyleDeclaration): boolean {
  if (style.display === "none") return false;
  if (style.position === "absolute" || style.position === "fixed") return false;
  if (el.classList.contains(PAGE_END_CLASS)) return false;
  return true;
}

/** Contenitori `flex-col` trasformati in blocchi, con il valore inline da ripristinare. */
let flattened: Array<[HTMLElement, string, string]> = [];

/**
 * Colonna flex che, come blocco, disporrebbe i figli allo stesso modo: nessuno spazio
 * fra i figli, nessun allineamento o giustificazione, figli a blocco senza allineamento
 * proprio. (Resta la differenza dei margini verticali, che nei blocchi si fondono: e'
 * irrilevante per le misure perche' si misura DOPO la trasformazione.)
 */
function isNeutralFlexColumn(el: Element, s: CSSStyleDeclaration): boolean {
  if (s.display !== "flex" || s.flexDirection !== "column" || s.flexWrap !== "nowrap") {
    return false;
  }
  if (!["normal", "stretch"].includes(s.alignItems)) return false;
  if (!["normal", "flex-start", "start"].includes(s.justifyContent)) return false;
  if (!["normal", "0px"].includes(s.rowGap)) return false;
  for (const child of Array.from(el.children)) {
    const c = getComputedStyle(child);
    if (!isInFlow(child, c)) continue;
    if (c.display.startsWith("inline")) return false;
    if (!["auto", "normal", "stretch"].includes(c.alignSelf)) return false;
    if (c.order !== "0") return false;
  }
  return true;
}

function flattenNeutralFlexColumns(root: Element): void {
  const targets = Array.from(root.querySelectorAll<HTMLElement>("*")).filter((el) =>
    isNeutralFlexColumn(el, getComputedStyle(el))
  );
  for (const el of targets) {
    flattened.push([
      el,
      el.style.getPropertyValue("display"),
      el.style.getPropertyPriority("display"),
    ]);
    el.style.setProperty("display", "block", "important");
  }
}

function restoreFlattened(): void {
  for (const [el, value, priority] of flattened) {
    if (value) el.style.setProperty("display", value, priority);
    else el.style.removeProperty("display");
  }
  flattened = [];
}

/**
 * Si puo' inserire un blocco fra i figli senza rompere il layout del contenitore, e un
 * salto forzato inserito li' viene rispettato da WebKit: il contenitore e' un blocco
 * (non flex: WebKit ignora i salti forzati nei contenitori flex; non tabella o griglia:
 * un elemento estraneo ne altererebbe il layout), e i figli sono blocchi (niente testo o
 * elementi inline, che verrebbero spezzati a meta' riga; niente contenitori i cui figli
 * sono tutti in posizione assoluta, che non occupano spazio nel flusso).
 */
function canDescend(el: Element, style: CSSStyleDeclaration): boolean {
  const d = style.display;
  if (d !== "block" && d !== "flow-root" && d !== "list-item") return false;

  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim() !== "") return false;
  }
  let inFlowChildren = 0;
  for (const child of Array.from(el.children)) {
    const s = getComputedStyle(child);
    if (!isInFlow(child, s)) continue;
    if (s.display.startsWith("inline")) return false;
    inFlowChildren++;
  }
  return inFlowChildren > 0;
}

function hasForcedBreakBefore(style: CSSStyleDeclaration): boolean {
  const v = style.breakBefore || (style as any).pageBreakBefore;
  return v === "page" || v === "always" || v === "left" || v === "right";
}

function hasForcedBreakAfter(style: CSSStyleDeclaration): boolean {
  const v = style.breakAfter || (style as any).pageBreakAfter;
  return v === "page" || v === "always" || v === "left" || v === "right";
}

function avoidsBreakInside(style: CSSStyleDeclaration): boolean {
  const v = style.breakInside || (style as any).pageBreakInside;
  return v === "avoid" || v === "avoid-page";
}

/**
 * Calcola i fogli. Tutte le misure sono prese sul layout non modificato; i blocchi
 * vengono inseriti solo alla fine, quindi inserirli non altera i calcoli.
 */
function computeBreaks(root: Element, footerHeight: number): PageBreak[] | null {
  const scale = root.closest(".print-layout")!.getBoundingClientRect().width / REPORT_WIDTH;
  if (scale <= 0) return null;
  const px = (v: number) => v / scale; // da px a video a px del report
  const rootTop = px(root.getBoundingClientRect().top);
  const top = (el: Element) => px(el.getBoundingClientRect().top) - rootTop;
  const bottom = (el: Element) => px(el.getBoundingClientRect().bottom) - rootTop;

  const capacity = SAFARI_SHEET_HEIGHT - footerHeight;
  const breaks: PageBreak[] = [];
  let pageStart = 0;
  let pendingBreak = false;

  // Contenitori che hanno al loro interno un salto forzato: vanno sempre visitati.
  const containsForced = new Set<Element>();
  root.querySelectorAll("*").forEach((el) => {
    const s = getComputedStyle(el);
    if (hasForcedBreakBefore(s) || hasForcedBreakAfter(s)) {
      for (let p = el.parentElement; p != null && p !== root; p = p.parentElement) {
        containsForced.add(p);
      }
    }
  });

  const breakBefore = (el: Element, style: CSSStyleDeclaration) => {
    // Il foglio nuovo inizia dove inizia l'elemento, margine compreso.
    const start = top(el) - parseFloat(style.marginTop || "0");
    if (start <= pageStart + EPS) return; // gia' in testa al foglio
    breaks.push({ before: el, parent: el.parentElement!, spacer: pageStart + capacity - start });
    pageStart = start;
  };

  const visit = (container: Element) => {
    for (const el of Array.from(container.children)) {
      const style = getComputedStyle(el);
      if (!isInFlow(el, style)) continue;

      if (style.display === "contents") {
        visit(el);
        continue;
      }

      if (pendingBreak || hasForcedBreakBefore(style)) {
        pendingBreak = false;
        breakBefore(el, style);
      }

      const height = bottom(el) - top(el);
      const fits = bottom(el) - pageStart <= capacity + EPS;

      if (fits && !containsForced.has(el)) {
        // Entra per intero nel foglio corrente.
      } else if (
        canDescend(el, style) &&
        (containsForced.has(el) || !avoidsBreakInside(style) || height > capacity)
      ) {
        // Si spezza al suo interno, come farebbe il browser.
        visit(el);
      } else if (!fits) {
        // Unita' indivisibile che non entra: passa intera al foglio dopo.
        breakBefore(el, style);
        if (bottom(el) - pageStart > capacity + EPS) {
          // Piu' alta di un foglio e non divisibile qui: il browser la spezzera' da
          // solo. Si riparte dal suo fondo per non accumulare l'errore sui fogli dopo.
          console.warn("[stampa Safari] blocco piu' alto di un foglio", el);
          pageStart = bottom(el);
        }
      }

      if (hasForcedBreakAfter(style)) pendingBreak = true;
    }
  };

  visit(root);

  // Ultimo foglio: pie' di pagina in fondo, senza salto dopo.
  breaks.push({
    before: null,
    parent: root,
    spacer: Math.max(0, pageStart + capacity - contentEnd(root, bottom)),
  });
  return breaks;
}

function contentEnd(root: Element, bottom: (el: Element) => number): number {
  let end = 0;
  for (const el of Array.from(root.children)) {
    const s = getComputedStyle(el);
    if (!isInFlow(el, s)) continue;
    end = Math.max(end, bottom(el) + parseFloat(s.marginBottom || "0"));
  }
  return end;
}

function makePageEnd(footer: Element, spacer: number, isLast: boolean): HTMLElement {
  const block = document.createElement("div");
  block.className = PAGE_END_CLASS + (isLast ? " " + PAGE_END_CLASS + "--last" : "");
  block.setAttribute("aria-hidden", "true");
  const space = document.createElement("div");
  space.style.height = `${Math.max(0, Math.floor(spacer))}px`;
  block.appendChild(space);
  block.appendChild(footer.cloneNode(true));
  return block;
}

/**
 * Impagina il report per Safari. Da chiamare con il report gia' in versione stampa
 * (`isPrinting` vero e ridisegni conclusi), subito prima di `window.print()`.
 * Restituisce true se l'impaginazione e' stata applicata.
 */
export function applySafariPagination(): boolean {
  undoSafariPagination();
  const layout = document.querySelector(".print-layout");
  const root = layout?.querySelector(":scope > tbody > tr > td");
  const footer = layout?.querySelector(":scope > tfoot .print-footer");
  if (layout == null || root == null || footer == null) return false;

  const restoreMedia = emulatePrintMedia();
  let breaks: PageBreak[] | null = null;
  try {
    flattenNeutralFlexColumns(root);
    const scale = layout.getBoundingClientRect().width / REPORT_WIDTH;
    const footerHeight = footer.getBoundingClientRect().height / scale;
    breaks = computeBreaks(root, footerHeight || 46);
  } catch (e) {
    console.error("[stampa Safari] impaginazione non riuscita", e);
  } finally {
    restoreMedia();
  }
  if (breaks == null) {
    restoreFlattened();
    return false;
  }

  breaks.forEach((b, i) => {
    const isLast = i === breaks!.length - 1;
    const block = makePageEnd(footer, b.spacer, isLast);
    if (b.before != null) b.parent.insertBefore(block, b.before);
    else b.parent.appendChild(block);
  });
  document.documentElement.classList.add(PAGINATED_CLASS);
  return true;
}

/** Rimuove tutto cio' che `applySafariPagination` ha aggiunto. */
export function undoSafariPagination(): void {
  document.querySelectorAll("." + PAGE_END_CLASS).forEach((el) => el.remove());
  restoreFlattened();
  document.documentElement.classList.remove(PAGINATED_CLASS);
}

/**
 * CSS attivo solo a impaginazione applicata (classe su <html>). Da iniettare una volta,
 * solo in WebKit.
 */
export const SAFARI_PAGINATION_CSS = `
@media print {
  /* I salti li decide l'impaginatore: quelli del CSS non devono sommarsi ai suoi. */
  .${PAGINATED_CLASS} .print-layout > tbody * {
    break-before: auto !important;
    break-after: auto !important;
    page-break-before: auto !important;
    page-break-after: auto !important;
  }
  .${PAGINATED_CLASS} .print-layout > tbody .${PAGE_END_CLASS} {
    break-after: page !important;
    page-break-after: always !important;
    break-inside: avoid;
  }
  .${PAGINATED_CLASS} .print-layout > tbody .${PAGE_END_CLASS}--last {
    break-after: auto !important;
    page-break-after: auto !important;
  }
  /* Il pie' di pagina unico in fondo e' sostituito dalle copie su ogni foglio. */
  .${PAGINATED_CLASS} .print-layout > tfoot {
    display: none !important;
  }
}
`;
